import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';

import { currencySymbols, formatMoneyWithSymbol } from '@williamhill/rosi-ui-shared/src/lib/money-formatter';
import { unitsValueMoney } from '@williamhill/rosi-ui-shared/src/lib/money';
import { DeviceType, ILeg } from '@williamhill/rosi-ui-shared/src/types';
import { fireGtmEvent } from '@williamhill/rosi-ui-shared/src/lib';
import Button from '@williamhill/rosi-ui-shared/src/components/button';
import { showFailureModal } from '@williamhill/rosi-ui-shared/src/components/modal';
import CashOutButton, { Modifier, CompleteLabel } from './cash-out-button';
import { Text as TypographyText } from '../../../components/typography';

export const CASH_OUT_MUTATION = gql`
  mutation cashOutBet($retailTicketId: String!, $cashOutValue: BigInt!) {
    cashOutBet(where: { retailTicketId: $retailTicketId, cashOutValue: $cashOutValue }) {
      id
    }
  }
`;

export type Props = {
  retailTicketId: string;
  cashOutValue?: number;
  currency: string;
  deviceType: DeviceType;
  legs: Leg[];
  onCashOutComplete(): void;
};

type State = 'default' | 'confirm' | 'loading' | 'complete';
type Leg = Pick<ILeg, 'result'>;

const displayCompleteDuration = 5000;

export default function CashOut({
  retailTicketId,
  currency,
  deviceType,
  cashOutValue = 0,
  legs,
  onCashOutComplete
}: Props) {
  const [cashOutBet] = useMutation(CASH_OUT_MUTATION, {
    variables: { retailTicketId, cashOutValue }
  });

  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const [state, setState] = useState<State>('default');

  const amountFormatted = formatCashOutValue({ cashOutValue, currency });

  useEffect(() => {
    if (state === 'complete') {
      clearTimeout(timeout.current);
      timeout.current = setTimeout(onCashOutComplete, displayCompleteDuration);
    }
    return () => clearTimeout(timeout.current);
  }, [state, onCashOutComplete]);

  const handleError = () => {
    onCashOutComplete();
    setState('default');

    fireGtmEvent('cash-out-error', { deviceType });

    showFailureModal({
      title: 'Cashout error',
      context: 'It was not possible to cashout your bet.'
    });
  };

  const handleCashOut = async () => {
    setState('loading');

    try {
      const response = await cashOutBet();

      if (response.data?.cashOutBet?.id) {
        fireGtmEvent('cash-out-success', { deviceType });
        setState('complete');
        return;
      }

      handleError();
    } catch (e) {
      handleError();
    }
  };

  return (
    whenLoading({ state }) ||
    whenComplete({ state }) || <CashOutOnConfirm {...{ state, amountFormatted, legs, setState, handleCashOut }} />
  );
}

function whenLoading({ state }: { state: State }) {
  return (
    state === 'loading' && (
      <CashOutButton modifiers="secondary" disabled>
        Working...
      </CashOutButton>
    )
  );
}

function whenComplete({ state }: { state: State }) {
  return (
    state === 'complete' && (
      <CashOutButton modifiers="secondary" disabled>
        <CompleteLabel />
      </CashOutButton>
    )
  );
}

function CashOutOnConfirm({
  state,
  amountFormatted,
  legs,
  setState,
  handleCashOut
}: {
  state: State;
  amountFormatted: string;
  legs: Leg[];
  setState(state: State): void;
  handleCashOut(): void;
}) {
  const [text, buttonModifiers, handleCashOutClick] =
    state === 'confirm'
      ? [`Confirm Cashout ${amountFormatted}`, ['secondary', 'fit'] as Modifier[], handleCashOut]
      : [`Cashout ${amountFormatted}`, ['primary'] as Modifier[], () => setState('confirm')];

  return (
    <Wrapper>
      <SettledLegs legs={legs} />
      {state === 'confirm' && (
        <Button buttonSize="standard" modifiers="secondaryInverted" onClick={() => setState('default')} text="Cancel" />
      )}
      <CashOutButton modifiers={buttonModifiers} onClick={handleCashOutClick}>
        {text}
      </CashOutButton>
    </Wrapper>
  );
}

function SettledLegs({ legs }: { legs: Leg[] }) {
  const settledLegs = legs.filter(l => l.result !== null);
  return (
    <Text as="div" size="lg" weight={400}>
      {settledLegs.length} of {legs.length} legs settled. Take early Cashout?
    </Text>
  );
}

export function formatCashOutValue({ cashOutValue, currency }: { cashOutValue: number; currency: string }) {
  return formatMoneyWithSymbol(unitsValueMoney(cashOutValue), currencySymbols[currency]);
}

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
`;

const Text = styled(TypographyText)`
  color: ${({ theme }) => theme.colors.white};
`;
